import React, { useState, useMemo, useCallback } from "react";
import SkeletonBase from "newui/components/Skeleton";
import { DeclineReasonModal } from "newui/components/ui/DeclineReasonModal";
import { showToast } from "utils/helpers";
import {
	useRevisionRequests,
	useApproveRevisionRequest,
	useDeclineRevisionRequest,
} from "../hooks/useRevisionRequests";
import { RevisionRequestsTable } from "./RevisionRequestsTable";
import type { EvaluationRevisionRequest } from "../types/revision-review.type";

interface RevisionRequestsTabProps {
	rfxId: number;
	evaluationId: number;
}

const REVISION_DECLINE_REASONS: Record<string, string> = {
	not_justified: "Revision request not justified",
	already_evaluated: "Already evaluated — no changes expected",
	deadline_passed: "Deadline for revisions has passed",
	other: "Other",
};

export const RevisionRequestsTab: React.FC<RevisionRequestsTabProps> = ({
	rfxId,
	evaluationId,
}) => {
	const [declineTarget, setDeclineTarget] = useState<EvaluationRevisionRequest | null>(null);

	const { data: requests, isLoading } = useRevisionRequests(rfxId, evaluationId);
	const approveMutation = useApproveRevisionRequest(rfxId, evaluationId);
	const declineMutation = useDeclineRevisionRequest(rfxId, evaluationId);

	const pendingCount = useMemo(() => {
		if (!requests) {
			return 0;
		}
		return requests.filter((r) => r.status === "pending").length;
	}, [requests]);

	const handleApprove = useCallback((request: EvaluationRevisionRequest) => {
		approveMutation.mutate(
			{
				rfxInviteId: request.rfx_invite_id,
				revisionRequestId: request.id,
				payload: {},
			},
			{
				onSuccess: () => {
					showToast("Revision request approved", "Success", true);
				},
				onError: () => {
					showToast("Failed to approve revision request", "Error", false);
				},
			}
		);
	}, [approveMutation]);

	const handleDecline = useCallback((request: EvaluationRevisionRequest) => {
		setDeclineTarget(request);
	}, []);

	const handleDeclineConfirm = useCallback((reason: string) => {
		if (!declineTarget) {
			return;
		}
		declineMutation.mutate(
			{
				rfxInviteId: declineTarget.rfx_invite_id,
				revisionRequestId: declineTarget.id,
				payload: { declined_reason: reason },
			},
			{
				onSuccess: () => {
					showToast("Revision request declined", "Success", true);
					setDeclineTarget(null);
				},
				onError: () => {
					showToast("Failed to decline revision request", "Error", false);
				},
			}
		);
	}, [declineTarget, declineMutation]);

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	if (!requests || requests.length === 0) {
		return (
			<div
				className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm"
				data-testid="revision-requests-empty"
			>
				No revision requests have been submitted by suppliers
			</div>
		);
	}

	return (
		<div data-testid="revision-requests-tab">
			<RevisionRequestsTable
				requests={requests}
				onApprove={handleApprove}
				onDecline={handleDecline}
				isApproving={approveMutation.isPending || declineMutation.isPending}
			/>

			<DeclineReasonModal
				isOpen={!!declineTarget}
				count={1}
				itemLabel="revision request"
				reasons={REVISION_DECLINE_REASONS}
				defaultReason="not_justified"
				onClose={() => setDeclineTarget(null)}
				onConfirm={handleDeclineConfirm}
				isLoading={declineMutation.isPending}
			/>
		</div>
	);
};

export const useRevisionRequestsPendingCount = (
	rfxId: number | undefined,
	evaluationId: number | undefined
): number => {
	const { data: requests } = useRevisionRequests(rfxId, evaluationId);
	return useMemo(() => {
		if (!requests) {
			return 0;
		}
		return requests.filter((r) => r.status === "pending").length;
	}, [requests]);
};
